import { motion, useReducedMotion } from "motion/react";
import { useContext, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { DAUER_BEDIENUNG, kurveAlsListe, kurveFuer } from "../welt/bewegung";
import { useWelt } from "../welt/einstellungen";
import { WeltKontext } from "../welt/kontext";
import { FEDER } from "./federn";

type Option<T extends string> = { wert: T; label: string };

/** Segment (Handoff Schiefer): radiogroup mit gleitendem Daumen (Feder), Pfeiltasten. Spur --pg-seg-bg
 *  Radius 10, gewaehlt Flaeche + e1 + feine Kante, Text 13, gewaehlt 700. */
export function Segment<T extends string>(props: {
  optionen: Option<T>[];
  /** null: noch nichts gewaehlt (kein Standard); dann ist das erste Segment per Tab erreichbar. */
  wert: T | null;
  onChange: (wert: T) => void;
  label: string;
  /** W73e (S7, D-382 H1): waehrend einer Anfrage keine Wahl (Optionen gesperrt, auch per Taste) */
  gesperrt?: boolean;
}) {
  const gruppe = useId();
  const ruhig = useReducedMotion();
  // W82: unter data-welt gleitet das Licht 500 ms (lebhaft Feder, ruhig expo) statt der Schiefer-Feder
  const welt = useContext(WeltKontext);
  const [{ fassung }] = useWelt();
  const gleiten = welt ? { duration: DAUER_BEDIENUNG.segmentLicht / 1000, ease: kurveAlsListe(kurveFuer("pille", fassung)) } : FEDER.segment;
  const index = props.optionen.findIndex((o) => o.wert === props.wert);
  // Passen die Segmente nicht nebeneinander (grosse Schrift, viele oder lange Namen), stehen sie
  // untereinander, statt Namen zu kuerzen oder mitten im Wort zu brechen (Verifier W4 Runde 4-6).
  // Gerechnet wird aus der natuerlichen Breite jedes Namens (unsichtbarer Messtext, einzeilig) plus
  // Innenabstand bzw. Mindestbreite, nicht aus der Reihe selbst: der gleitende Daumen und der
  // aktuelle Zustand (Reihe oder Stapel) beeinflussen das Ergebnis so nicht.
  const huelle = useRef<HTMLDivElement>(null);
  const reihe = useRef<HTMLDivElement>(null);
  const [gestapelt, setGestapelt] = useState(false);
  useLayoutEffect(() => {
    const h = huelle.current;
    const r = reihe.current;
    if (!h || !r) return;
    const px = (w: string) => parseFloat(w) || 0;
    const pruefen = () => {
      const spur = getComputedStyle(r);
      let noetig = px(spur.paddingLeft) + px(spur.paddingRight);
      for (const knopf of r.querySelectorAll<HTMLElement>(".segment")) {
        const mass = knopf.querySelector<HTMLElement>(".segment-mass");
        const st = getComputedStyle(knopf);
        noetig += Math.max(px(st.minWidth), (mass?.offsetWidth ?? 0) + px(st.paddingLeft) + px(st.paddingRight));
      }
      setGestapelt(noetig > h.clientWidth);
    };
    pruefen();
    const beobachter = new ResizeObserver(pruefen);
    beobachter.observe(h);
    for (const mass of r.querySelectorAll(".segment-mass")) beobachter.observe(mass);
    return () => beobachter.disconnect();
  }, [props.optionen]);

  // W56 (PG-A11Y-002): die per Taste gewaehlte Option bekommt den Fokus, sobald `wert` sie zeigt; die Eltern
  // koennen den Wert auch spaeter setzen. Ein Wert von aussen ohne Taste zieht den Fokus nicht nach.
  const knoepfe = useRef(new Map<T, HTMLButtonElement>());
  const ziel = useRef<T | null>(null);
  useLayoutEffect(() => {
    if (ziel.current === null || ziel.current !== props.wert) return;
    knoepfe.current.get(ziel.current)?.focus();
    ziel.current = null;
  }, [props.wert]);

  function taste(e: KeyboardEvent) {
    if (props.gesperrt) return;
    const schritt = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!schritt) return;
    e.preventDefault();
    // Schnelle Tasten rechnen von der zuletzt per Taste gewaehlten Option, auch wenn `wert` sie noch nicht zeigt.
    // Ohne Wahl fuehrt rechts zum ersten, links zum letzten Segment
    const basis = ziel.current !== null ? props.optionen.findIndex((o) => o.wert === ziel.current) : index;
    const von = basis >= 0 ? basis : schritt > 0 ? -1 : 0;
    const neu = props.optionen[(von + schritt + props.optionen.length) % props.optionen.length];
    if (!neu) return;
    ziel.current = neu.wert;
    props.onChange(neu.wert);
    // zeigt der Wert die Option schon (dieselbe Option noch einmal), gleich dorthin
    if (neu.wert === props.wert) {
      knoepfe.current.get(neu.wert)?.focus();
      ziel.current = null;
    }
  }

  return (
    <div ref={huelle} className="min-w-0">
      <div
        ref={reihe}
        data-gestapelt={gestapelt ? "" : undefined}
        role="radiogroup"
        aria-label={props.label}
        onKeyDown={taste}
        onBlur={(e) => {
          // wer die Gruppe vor dem Wert verlaesst, wird nicht zurueckgeholt
          if (!e.currentTarget.contains(e.relatedTarget)) ziel.current = null;
        }}
        className={`segment-reihe flex min-h-(--seg-h) w-full max-w-full rounded-(--r-seg) bg-pg-seg-bg p-(--seg-pad) md:w-fit${props.gesperrt ? " cursor-not-allowed opacity-40" : ""}`}
      >
        {props.optionen.map((o, i) => {
          const aktiv = o.wert === props.wert;
          return (
            <button
              key={o.wert}
              ref={(knopf) => {
                if (knopf) knoepfe.current.set(o.wert, knopf);
                else knoepfe.current.delete(o.wert);
              }}
              type="button"
              role="radio"
              aria-checked={aktiv}
              tabIndex={aktiv || (index < 0 && i === 0) ? 0 : -1}
              disabled={props.gesperrt}
              onClick={() => !props.gesperrt && props.onChange(o.wert)}
              className={`segment relative min-h-(--seg-h) min-w-(--touch) flex-auto px-1 t-footnote md:min-w-(--seg-min-w) md:flex-none md:px-3 ${aktiv ? "font-bold text-pg-text" : "font-medium text-pg-text-2"}`}
            >
              {aktiv && (
                <motion.span
                  layoutId={ruhig ? undefined : `seg-${gruppe}`}
                  // W82-3: das Licht gleitet nur beim Wechsel des Werts, nicht beim Umfließen (etwa nach größerer Schrift)
                  layoutDependency={props.wert}
                  transition={gleiten}
                  className="segment-daumen absolute inset-0 rounded-(--r-seg-thumb)"
                  aria-hidden
                />
              )}
              {/* Nicht kuerzen: passt ein Name nicht, stehen die Segmente untereinander (s. oben) */}
              <span className="segment-name relative block py-0.5 text-center hyphens-auto">{o.label}</span>
              <span aria-hidden className="segment-mass">
                {o.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
